import { makeAutoObservable, flow } from "mobx"
import { apiFetch, setAuthToken } from "../lib/api.js"
import { getTelegramWebApp, isTelegramMiniApp } from "../lib/telegram.js"
import type { RootStore } from "./RootStore.js"

type AuthState =
  | { status: "idle" }
  | { status: "authenticating" }
  | { status: "authenticated"; userId: string; isAdmin: boolean }
  | { status: "error"; message: string }

export class AuthStore {
  state: AuthState = { status: "idle" }
  rootStore: RootStore

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this, { rootStore: false })
  }

  get isAuthenticated(): boolean {
    return this.state.status === "authenticated"
  }

  get userId(): string | null {
    return this.state.status === "authenticated" ? this.state.userId : null
  }

  authenticate = flow(function* (this: AuthStore) {
    this.state = { status: "authenticating" }

    if (isTelegramMiniApp()) {
      const webapp = getTelegramWebApp()
      if (!webapp) {
        this.state = { status: "error", message: "Telegram WebApp not available" }
        return
      }

      const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch("/api/auth/miniapp", {
        method: "POST",
        body: JSON.stringify({ initData: webapp.initData }),
      })

      if (result.status === "ok") {
        const data = result.data as { user: { id: string; isAdmin: boolean }; token?: string }
        if (data.token) {
          setAuthToken(data.token)
        }
        this.state = { status: "authenticated", userId: data.user.id, isAdmin: data.user.isAdmin }
      } else {
        this.state = { status: "error", message: result.message }
      }
    } else {
      // Try session cookie
      const result: Awaited<ReturnType<typeof apiFetch>> = yield apiFetch("/api/auth/me")
      if (result.status === "ok") {
        const data = result.data as { user: { userId: string; isAdmin: boolean } }
        this.state = { status: "authenticated", userId: data.user.userId, isAdmin: data.user.isAdmin }
      } else {
        this.state = { status: "idle" }
      }
    }
  })
}
